<template>
  <div class="m-2">
    <h1 class="mainheader d-flex justify-content-left p-2">
      <span>Motif Analysis</span>
      <img
        src="@/assets/diamond.svg"
        height="20px"
        alt="Premium"
        class="ml-2"
      />
    </h1>

    <div class="pl-2 text-left">
      <div class="updated-note">
        <p class="mb-0">
          <b>Motif Analysis (updated {{ updatedLabel }}):</b> improved
          statistics and documentation.
          <a :href="documentationUrl" target="_blank" rel="noopener"
            >Read the documentation<span class="sr-only">
              (opens in a new tab)</span
            ></a
          >
          &middot;
          <button
            type="button"
            class="btn btn-link p-0 align-baseline"
            :aria-expanded="showWhatChanged ? 'true' : 'false'"
            aria-controls="whatChanged"
            @click="showWhatChanged = !showWhatChanged"
          >
            {{ showWhatChanged ? "Hide what changed" : "What changed" }}
          </button>
        </p>
        <p v-if="showWhatChanged" id="whatChanged" class="mb-0 mt-2">
          {{ whatChangedText }}
          <a :href="documentationUrl" target="_blank" rel="noopener"
            >Method details<span class="sr-only"> (opens in a new tab)</span></a
          >.
        </p>
      </div>
      <p class="mt-2">
        <b>Statistical Motif Analysis in Promoter or Upstream Gene Sequences</b>
      </p>
      <p>
        The program compares the frequencies of 6-mer "words" in your query set
        of sequences (on both strands) with the frequencies of the words in the
        genomic sequence set of Arabidopsis loci (TAIR12 or Araport11
        annotation), each containing 500, 1000 or 3000 bp upstream of the start
        of each gene. The TAIR12 set leaves out the rDNA repeat units
        (AT1R..AT5R loci), which are highly repetitive. You can type
        in sets of AGI locus identifiers (e.g. At1g01030) or sets of FASTA
        sequences. Make sure each FASTA header is formatted as such: fasta
        symbol (&gt;), immediately followed by a unique ID, a space, then all
        other descriptions (e.g. &gt;ABCD1.1 my gene). Ensure that there are no
        sequences appearing more than once in your query set.
      </p>
      <p>
        <a
          href="#help"
          :aria-expanded="showHelp ? 'true' : 'false'"
          aria-controls="help"
          @click.prevent="showHelp = !showHelp"
          >{{ showHelp ? "Hide" : "Show" }} help: how to read the results</a
        >
      </p>
      <div
        v-if="showHelp"
        id="help"
        ref="helpPanel"
        tabindex="-1"
        class="help-box border rounded p-3 mb-3"
      >
        <b>Output columns</b>
        <ul>
          <li><b>oligoMer</b>: the 6-base word, counted on both strands.</li>
          <li>
            <b>Query seqs</b>: number of query sequences containing the word,
            out of the query size, with the percentage.
          </li>
          <li>
            <b>Genomic seqs</b>: number of sequences in the background dataset
            containing the word, out of the dataset size, with the percentage.
          </li>
          <li>
            <b>Fold</b>: query percentage divided by genomic percentage. Above 1
            means the word is more common in your query set than in the
            genome.
          </li>
          <li>
            <b>Direction</b>: <i>enriched</i> when the query percentage is
            above the genomic percentage, <i>depleted</i> when below. Use the
            "enriched only" filter to hide depleted words.
          </li>
          <li>
            <b>p-value</b>: exact two-sided binomial test of the query count
            against the genomic rate (n = query size, k = query sequences with
            the word, p = genomic fraction). Small values mean the query count is
            unlikely to arise by chance in either direction.
          </li>
          <li>
            <b>FDR</b>: Benjamini-Hochberg false discovery rate across all words
            reported for this query. A common cutoff is FDR &lt; 0.05.
          </li>
          <li>
            <b># in query set / # in genomic set</b>: absolute occurrence
            counts, both strands, for reference.
          </li>
        </ul>
        <b>Caveats</b>
        <ul>
          <li>
            <b>Independence.</b> Results can be inflated when the query contains
            tandemly duplicated or closely related genes, because their
            promoters are often similar. Reduce such clusters to one
            representative gene, or treat those hits with caution.
          </li>
          <li>
            Only words present in at least {{ minSeqs }} query sequences are
            tested. The FDR is computed over the words that were tested.
          </li>
          <li>
            The statistics changed in {{ updatedLabel }}; see
            <button
              type="button"
              class="btn btn-link p-0 align-baseline"
              @click="openWhatChanged"
            >
              What changed
            </button>
            above if you used the tool before then.
          </li>
        </ul>
        <p class="mb-0">
          Full documentation, including a worked example and the method
          details:
          <a :href="documentationUrl" target="_blank" rel="noopener"
            >TAIR Motif Finder help page<span class="sr-only">
              (opens in a new tab)</span
            ></a
          >.
        </p>
      </div>
    </div>

    <div class="container p-2 border rounded shadow-sm form-bg">
      <form @submit.prevent="handleSubmit" @reset.prevent="handleReset">
        <!-- Query input -->
        <fieldset class="border border-dark rounded p-2">
          <legend class="fieldset-title">
            Enter AGI locus identifiers or FASTA sequences
          </legend>
          <div class="form-group row">
            <label for="motifInput" class="col-sm-4 col-form-label text-left"
              >Input:</label
            >
            <div class="col-sm-8">
              <textarea
                id="motifInput"
                v-model="form.input"
                class="form-control"
                rows="10"
                :placeholder="inputPlaceholder"
              ></textarea>
            </div>
          </div>
          <div class="form-group row">
            <label for="motifFile" class="col-sm-4 col-form-label text-left"
              >Or upload a file:</label
            >
            <div class="col-sm-8 text-left">
              <input
                id="motifFile"
                ref="fileInput"
                type="file"
                class="form-control-file"
                @change="handleFileUpload"
              />
            </div>
          </div>
        </fieldset>

        <!-- Dataset -->
        <fieldset class="border border-dark rounded p-2 mt-4">
          <legend class="fieldset-title">Choose a Dataset</legend>
          <div class="form-group row">
            <label for="dataset" class="col-sm-4 col-form-label text-left"
              >Background dataset:</label
            >
            <div class="col-sm-8">
              <select id="dataset" v-model="form.dataset" class="form-control">
                <option
                  v-for="option in datasetOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.text }}
                </option>
              </select>
            </div>
          </div>
        </fieldset>

        <!-- Output -->
        <fieldset class="border border-dark rounded p-2 mt-4">
          <legend class="fieldset-title">Output</legend>
          <div class="form-group row">
            <span id="outputTypeLabel" class="col-sm-4 col-form-label text-left"
              >Output type:</span
            >
            <div
              class="col-sm-8 d-flex align-items-center"
              role="radiogroup"
              aria-labelledby="outputTypeLabel"
            >
              <div class="form-check form-check-inline">
                <input
                  id="outputHtml"
                  v-model="form.outputType"
                  class="form-check-input"
                  type="radio"
                  name="outputType"
                  value="html"
                />
                <label class="form-check-label" for="outputHtml">HTML</label>
              </div>
              <div class="form-check form-check-inline">
                <input
                  id="outputText"
                  v-model="form.outputType"
                  class="form-check-input"
                  type="radio"
                  name="outputType"
                  value="text"
                />
                <label class="form-check-label" for="outputText">Text</label>
              </div>
            </div>
          </div>
        </fieldset>

        <div class="form-group row">
          <div class="col-sm-4 offset-sm-1 d-flex justify-content-left mt-2">
            <button
              type="submit"
              class="btn btn-primary"
              :disabled="loading"
              :aria-busy="loading ? 'true' : 'false'"
            >
              <span
                v-if="loading"
                class="spinner-border spinner-border-sm mr-2"
                aria-hidden="true"
              ></span>
              Run Motif Analysis
            </button>
            <button type="reset" class="ml-4 btn btn-secondary">Reset</button>
          </div>
        </div>
      </form>
    </div>

    <!-- Screen-reader announcements for the request lifecycle -->
    <div class="sr-only" role="status" aria-live="polite">
      {{ statusMessage }}
    </div>

    <!-- Errors -->
    <div v-if="errorMessage" class="alert alert-danger mt-3 text-left" role="alert">
      <div>{{ errorMessage }}</div>
      <div v-if="errorDetails.invalidLines && errorDetails.invalidLines.length" class="mt-2">
        Invalid bases have been highlighted:
        <div
          v-for="bad in errorDetails.invalidLines"
          :key="bad.lineNumber"
          class="invalid-line"
        >
          <span class="text-danger">line {{ bad.lineNumber }}:</span>
          <span
            v-for="(ch, i) in bad.line.split('')"
            :key="i"
            :class="/[ACGT]/i.test(ch) ? '' : 'text-danger font-weight-bold'"
            >{{ ch }}</span
          >
        </div>
      </div>
      <div v-if="errorDetails.notFound && errorDetails.notFound.length" class="mt-2">
        Not found: {{ errorDetails.notFound.join(" ") }}
      </div>
    </div>

    <!-- Results -->
    <div v-if="result" class="container p-2 border rounded shadow-sm mt-3 text-left">
      <div class="d-flex justify-content-between align-items-center flex-wrap">
        <h2 class="mainheader m-0">Results</h2>
        <button
          type="button"
          class="btn btn-outline-secondary btn-sm"
          @click="downloadText"
        >
          Download as text
        </button>
      </div>

      <p class="dataset-line mt-2 mb-1">
        <b>Dataset:</b> {{ datasetLine }}
      </p>
      <p class="mb-1">
        <b>Query:</b> {{ result.nodeSize }} sequences analyzed;
        {{ result.results.length }} oligoMers present in at least
        {{ result.minSeqsWithOligo }} of them were tested.
      </p>
      <p class="mb-2 small note-text">
        Statistics: {{ methodInfo.test }} against the genomic rate, with
        {{ methodInfo.correction }} across the tested oligoMers. Rows are
        sorted by p-value.
        <a href="#help" @click.prevent="openHelp">Help: how to read the results</a>
      </p>

      <div v-if="result.results.length === 0" class="alert alert-info">
        No 6-mer occurred in {{ result.minSeqsWithOligo }} or more of the
        {{ result.nodeSize }} analyzed sequences.
      </div>

      <div v-else-if="form.outputType === 'text'">
        <pre class="text-output">{{ textOutput }}</pre>
      </div>

      <div v-else>
        <div
          class="d-flex flex-wrap align-items-center mb-2 filters"
          role="group"
          aria-label="Result filters"
        >
          <div class="form-check form-check-inline">
            <input
              id="enrichedOnly"
              v-model="filters.enrichedOnly"
              class="form-check-input"
              type="checkbox"
            />
            <label class="form-check-label" for="enrichedOnly"
              >Enriched only</label
            >
          </div>
          <div class="form-check form-check-inline">
            <input
              id="significantOnly"
              v-model="filters.significantOnly"
              class="form-check-input"
              type="checkbox"
            />
            <label class="form-check-label" for="significantOnly"
              >FDR &lt; {{ fdrCutoff }} only</label
            >
          </div>
          <span class="small note-text ml-2" aria-live="polite">
            {{ filteredRows.length }} of {{ result.results.length }} oligoMers
            shown
          </span>
        </div>

        <div class="table-responsive">
          <table class="table table-sm table-striped table-bordered results-table">
            <caption class="sr-only">
              Motif analysis results: 6-mer words sorted by p-value, with query
              and genomic frequencies, fold change, direction, p-value and
              false discovery rate. Rows with FDR below {{ fdrCutoff }} are
              marked as significant.
            </caption>
            <thead>
              <tr>
                <th scope="col">oligoMer</th>
                <th scope="col" class="text-right">Query seqs</th>
                <th scope="col" class="text-right">Genomic seqs</th>
                <th scope="col" class="text-right">Fold</th>
                <th scope="col">Direction</th>
                <th scope="col" class="text-right">p-value</th>
                <th scope="col" class="text-right">FDR</th>
                <th scope="col" class="text-right"># in query set</th>
                <th scope="col" class="text-right"># in genomic set</th>
                <th scope="col">
                  Query sequences containing this oligoMer
                  <span class="d-block small font-weight-normal"
                    >(locus links open in a new tab)</span
                  >
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in visibleRows"
                :key="row.oligo"
                :class="{ 'row-significant': row.fdr < fdrCutoff }"
              >
                <th scope="row" class="font-monospace font-weight-normal">
                  {{ row.oligo }}
                </th>
                <td class="text-right">
                  {{ row.querySeqsWithOligo }}/{{ result.nodeSize }}
                  <span class="pct small"
                    >({{ formatPercent(row.queryFraction) }})</span
                  >
                </td>
                <td class="text-right">
                  {{ row.backgroundSeqsWithOligo }}/{{ result.numBackgroundSeqs }}
                  <span class="pct small"
                    >({{ formatPercent(row.backgroundFraction) }})</span
                  >
                </td>
                <td class="text-right">{{ formatFold(row.foldEnrichment) }}</td>
                <td>
                  <span :class="directionClass(row.direction)">{{
                    directionLabel(row.direction)
                  }}</span>
                </td>
                <td class="text-right font-monospace">
                  {{ formatPValue(row.pValue) }}
                </td>
                <td class="text-right font-monospace">
                  {{ formatPValue(row.fdr)
                  }}<span v-if="row.fdr < fdrCutoff" class="sr-only">
                    (significant)</span
                  >
                </td>
                <td class="text-right">{{ row.queryCount }}</td>
                <td class="text-right">{{ row.backgroundCount }}</td>
                <td class="seq-list">
                  <button
                    type="button"
                    class="btn btn-link btn-sm p-0 align-baseline"
                    :aria-expanded="isExpanded(row.oligo) ? 'true' : 'false'"
                    :aria-controls="`seqs-${row.oligo}`"
                    @click="toggleSequences(row.oligo)"
                  >
                    {{ row.sequences.length }}
                    {{ result.inputType === "loci" ? "loci" : "sequences" }}
                    <span aria-hidden="true">{{
                      isExpanded(row.oligo) ? "▾" : "▸"
                    }}</span>
                  </button>
                  <div
                    v-if="isExpanded(row.oligo)"
                    :id="`seqs-${row.oligo}`"
                    class="mt-1"
                  >
                    <span v-for="id in row.sequences" :key="id" class="mr-2">
                      <router-link
                        v-if="isLocus(id)"
                        :to="{ name: 'LocusDetail', query: { name: id } }"
                        target="_blank"
                        >{{ id }}</router-link
                      >
                      <template v-else>{{ id }}</template>
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <div
            v-if="hiddenRowCount > 0"
            class="d-flex align-items-center justify-content-left mt-3 mb-2"
          >
            <span class="mr-3">
              Showing {{ visibleRows.length }} of
              {{ filteredRows.length }} oligoMers (lowest p-values first).
            </span>
            <button
              type="button"
              class="btn btn-outline-secondary btn-sm"
              @click="showMoreRows"
            >
              Show {{ Math.min(rowPageSize, hiddenRowCount) }} more
            </button>
            <button
              type="button"
              class="btn btn-link btn-sm"
              @click="rowLimit = filteredRows.length"
            >
              Show all
            </button>
          </div>
        </div>
      </div>

      <hr />
      <p v-if="result.inputType === 'loci'">
        {{ result.nodeSize }} sequences analyzed:
        <span class="seq-list">{{ result.analyzedLoci.join(" ") }}</span>
      </p>
      <p v-else>{{ result.nodeSize }} sequences analyzed.</p>
      <p v-if="result.notFound && result.notFound.length" class="text-danger">
        The following sequences were not found:
        <span class="seq-list">{{ result.notFound.join(" ") }}</span>
      </p>
    </div>
  </div>
</template>

<script>
import PostsService from "@/services/PostsService";

const LOCUS_RE = /^AT[1-5CM]G\d{5}$/i;

// TAIR3-973: curator-written documentation. Confluence page "Motif Finder"
// (COM space, page 42217599), updated September 2026 for the new statistics;
// its "What changed in September 2026" section is the change note.
const DOCUMENTATION_URL =
  "https://phoenixbioinformatics.atlassian.net/wiki/spaces/COM/pages/42217599/Motif+Finder";
const UPDATED_LABEL = "September 2026";
// "What changed" note, text agreed in TAIR3-973.
const WHAT_CHANGED_TEXT =
  "Earlier versions used a documented ranking score, and the new version uses standard p-values with enrichment direction and FDR. The practical effect for users: the strongest hits were roughly right but mixed enriched and depleted motifs, and weaker hits overstated significance. Recommend re-running earlier analyses.";
const FDR_CUTOFF = 0.05;

// Background datasets, named by annotation (TAIR3-648). Values are the server's
// dataset ids; the first one this server has is the default.
const DATASET_OPTIONS = [
  { text: "TAIR12: 500 bp upstream of gene", value: "TAIR12_500" },
  { text: "TAIR12: 1000 bp upstream of gene", value: "TAIR12_1000" },
  { text: "TAIR12: 3000 bp upstream of gene", value: "TAIR12_3000" },
  { text: "Araport11: 500 bp upstream of gene", value: "Araport11_500" },
  { text: "Araport11: 1000 bp upstream of gene", value: "Araport11_1000" },
  { text: "Araport11: 3000 bp upstream of gene", value: "Araport11_3000" },
];

const defaultForm = (dataset = DATASET_OPTIONS[0].value) => ({
  input: "",
  dataset,
  outputType: "html",
});

/**
 * True when a response carries the TAIR3-973 statistics (method description
 * and, for every row, direction and FDR). Older API builds return only the
 * legacy point probability, which must not be labelled as an exact test.
 */
function hasCurrentStatistics(data) {
  if (!data || !data.method || !Array.isArray(data.results)) return false;
  return data.results.every(
    (r) =>
      typeof r.fdr === "number" &&
      typeof r.direction === "string" &&
      typeof r.pValue === "number"
  );
}

export default {
  name: "MotifFinder",
  data() {
    return {
      form: defaultForm(),
      inputPlaceholder:
        "At1g01030\nAt1g01040\n...\n\nor\n\n>ABCD1.1 my gene\nACGTACGT...",
      datasetOptions: DATASET_OPTIONS,
      loading: false,
      errorMessage: "",
      errorDetails: {},
      result: null,
      showHelp: false,
      showWhatChanged: false,
      filters: { enrichedOnly: false, significantOnly: false },
      expanded: {},
      // Results can hold up to 4096 rows; render them in pages so large
      // queries do not freeze the tab. Gene lists stay collapsed until clicked.
      rowPageSize: 100,
      rowLimit: 100,
      documentationUrl: DOCUMENTATION_URL,
      whatChangedText: WHAT_CHANGED_TEXT,
      updatedLabel: UPDATED_LABEL,
      fdrCutoff: FDR_CUTOFF,
      minSeqs: 3,
    };
  },
  computed: {
    /** One-line dataset description shown at the top of the results (TAIR3-973 item 4). */
    datasetLine() {
      if (!this.result) return "";
      const n = Number(this.result.numBackgroundSeqs).toLocaleString();
      return `${this.result.datasetLabel || `upstream ${this.result.upstream} bp`}, ${n} sequences`;
    },
    /** Method description from the API (results without it are rejected in handleSubmit). */
    methodInfo() {
      return (this.result && this.result.method) || {};
    },
    /** Text for the polite live region: announces running / finished / failed. */
    statusMessage() {
      if (this.loading) return "Running motif analysis, please wait.";
      if (this.errorMessage) return `Motif analysis failed: ${this.errorMessage}`;
      if (this.result) {
        return `Motif analysis complete. ${this.result.nodeSize} sequences analyzed, ${this.result.results.length} oligoMers tested. Results are shown below.`;
      }
      return "";
    },
    /** Rows after the enriched-only / FDR filters, still sorted by p-value. */
    filteredRows() {
      if (!this.result) return [];
      return this.result.results.filter((r) => {
        if (this.filters.enrichedOnly && r.direction !== "enriched") return false;
        if (this.filters.significantOnly && !(r.fdr < this.fdrCutoff)) return false;
        return true;
      });
    },
    /** Rows currently rendered in the HTML table. */
    visibleRows() {
      return this.filteredRows.slice(0, this.rowLimit);
    },
    /** Number of filtered rows not yet rendered. */
    hiddenRowCount() {
      return Math.max(0, this.filteredRows.length - this.rowLimit);
    },
    /** Complete result set in the tab-separated text format (all rows, no filters). */
    textOutput() {
      if (!this.result) return "";
      const r = this.result;
      const lines = [
        `Dataset: ${this.datasetLine}`,
        `Query: ${r.nodeSize} sequences analyzed; ${r.results.length} oligoMers present in at least ${r.minSeqsWithOligo} of them were tested.`,
        `Statistics: ${this.methodInfo.test} against the genomic rate; ${this.methodInfo.correction} across the tested oligoMers. Rows are sorted by p-value.`,
        `Note: statistics updated ${this.updatedLabel} (standard p-values with enrichment direction and FDR replace the earlier ranking score). Documentation: ${this.documentationUrl}`,
        "",
        [
          "oligoMer",
          "query_seqs_with_oligo/query_size",
          "genomic_seqs_with_oligo/genomic_size",
          "query_percent",
          "genomic_percent",
          "fold",
          "direction",
          "p_value",
          "fdr",
          "count_in_query_set",
          "count_in_genomic_set",
          "query_sequences_containing_oligo",
        ].join("\t"),
      ];
      for (const row of r.results) {
        lines.push(
          [
            row.oligo,
            `${row.querySeqsWithOligo}/${r.nodeSize}`,
            `${row.backgroundSeqsWithOligo}/${r.numBackgroundSeqs}`,
            this.formatPercent(row.queryFraction),
            this.formatPercent(row.backgroundFraction),
            this.formatFold(row.foldEnrichment),
            row.direction,
            // raw values so downstream cutoffs are applied to the same numbers
            // the page uses, not to a two-decimal rounding
            row.pValue,
            row.fdr,
            row.queryCount,
            row.backgroundCount,
            row.sequences.join("  "),
          ].join("\t")
        );
      }
      lines.push("");
      if (r.inputType === "loci") {
        lines.push(`${r.nodeSize} sequences analyzed: ${r.analyzedLoci.join("  ")}`);
        if (r.notFound.length) {
          lines.push(
            `The following sequences were not found: ${r.notFound.join("\t")}`
          );
        }
      } else {
        lines.push(`${r.nodeSize} sequences analyzed.`);
      }
      return lines.join("\n");
    },
  },
  watch: {
    /** Reset paging whenever the filters change so the first page is shown. */
    filters: {
      deep: true,
      handler() {
        this.rowLimit = this.rowPageSize;
      },
    },
  },
  created() {
    this.loadAvailableDatasets();
  },
  methods: {
    /**
     * Offer only the datasets whose files are on this server, so the page can
     * be deployed before a server has the TAIR12 files. If the list cannot be
     * fetched, or nothing is available, every option stays and a submit
     * reports the missing file.
     */
    async loadAvailableDatasets() {
      try {
        const response = await PostsService.getMotifDatasets();
        const datasets = (response.data && response.data.datasets) || [];
        const available = new Set(
          datasets.filter((d) => d.available).map((d) => d.id)
        );
        const options = DATASET_OPTIONS.filter((o) => available.has(o.value));
        if (!options.length) return;
        this.datasetOptions = options;
        if (!available.has(this.form.dataset)) {
          this.form.dataset = options[0].value;
        }
      } catch (error) {
        // keep the full list
      }
    },
    /** True when an id looks like an AGI locus (AT1G01010), so it can link to Locus Detail. */
    isLocus(id) {
      return LOCUS_RE.test(id);
    },
    /** Two-decimal scientific notation for p-values and FDR. */
    formatPValue(p) {
      return Number(p).toExponential(2);
    },
    /** Fraction as a percentage with one decimal. */
    formatPercent(f) {
      return `${(Number(f) * 100).toFixed(1)}%`;
    },
    /** Fold ratio with two decimals; "inf" when the word is absent from the genome. */
    formatFold(f) {
      if (f === null || f === undefined) return "inf";
      return Number(f).toFixed(2);
    },
    /** Display label for a row's direction. */
    directionLabel(d) {
      if (d === "enriched") return "enriched";
      if (d === "depleted") return "depleted";
      return "—";
    },
    /** CSS class for a row's direction (colours chosen for WCAG AA contrast on the striped rows). */
    directionClass(d) {
      if (d === "enriched") return "direction-enriched";
      if (d === "depleted") return "direction-depleted";
      return "direction-none";
    },
    /** Whether a row's gene list is expanded. */
    isExpanded(oligo) {
      return Boolean(this.expanded[oligo]);
    },
    /** Expand or collapse a row's gene list. */
    toggleSequences(oligo) {
      this.$set(this.expanded, oligo, !this.expanded[oligo]);
    },
    /** Open the "What changed" note under the banner and scroll to it (used from the help panel). */
    openWhatChanged() {
      this.showWhatChanged = true;
      this.$nextTick(() => {
        const el = document.getElementById("whatChanged");
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
    },
    /** Open the help section, scroll it into view and move focus to it (used from the results block). */
    openHelp() {
      this.showHelp = true;
      this.$nextTick(() => {
        const el = this.$refs.helpPanel;
        if (!el) return;
        if (el.scrollIntoView) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
        if (el.focus) {
          el.focus({ preventScroll: true });
        }
      });
    },
    /** Reveal the next page of result rows. */
    showMoreRows() {
      this.rowLimit += this.rowPageSize;
    },
    /** Read an uploaded file's text into the input textarea (same as Blast). */
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.form.input = e.target.result;
        };
        reader.readAsText(file);
      }
    },
    /** Validate locally, POST to the server, and store the result or error details. */
    async handleSubmit() {
      this.errorMessage = "";
      this.errorDetails = {};
      this.result = null;
      this.expanded = {};
      this.rowLimit = this.rowPageSize;
      if (!this.form.input.trim()) {
        this.errorMessage =
          "Please enter locus identifiers or FASTA sequences, or upload a file.";
        return;
      }
      this.loading = true;
      try {
        const response = await PostsService.runMotifAnalysis({
          input: this.form.input,
          dataset: this.form.dataset,
        });
        const data = response.data || {};
        if (!hasCurrentStatistics(data)) {
          // An API build that predates TAIR3-973 returns the legacy point
          // probability without direction or FDR. Do not present that under
          // the new labels.
          this.errorMessage =
            "The analysis service returned results in an older format, so they cannot be shown with the current statistics. Please try again in a few minutes.";
          return;
        }
        this.result = data;
        this.minSeqs = this.result.minSeqsWithOligo || this.minSeqs;
      } catch (error) {
        const data = (error.response && error.response.data) || {};
        this.errorMessage =
          data.error || "Motif analysis failed. Please try again later.";
        this.errorDetails = {
          invalidLines: data.invalidLines,
          notFound: data.notFound,
        };
      } finally {
        this.loading = false;
      }
    },
    /** Clear the form, file input, errors and results. */
    handleReset() {
      this.form = defaultForm(this.datasetOptions[0].value);
      this.errorMessage = "";
      this.errorDetails = {};
      this.result = null;
      this.expanded = {};
      this.filters = { enrichedOnly: false, significantOnly: false };
      this.rowLimit = this.rowPageSize;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },
    /** Download the complete result set (all rows) in the text format. */
    downloadText() {
      const blob = new Blob([this.textOutput], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const annotation = this.result.annotation
        ? `${this.result.annotation}_`
        : "";
      link.download = `motif_analysis_${annotation}${this.result.upstream}bp.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped>
.mainheader {
  font-size: 16pt;
  font-weight: bold;
  color: #930;
  margin: 0;
}
.fieldset-title {
  font-size: 1rem;
  font-weight: bold;
  width: auto;
  text-align: left;
  padding: 0 0.25rem;
  margin: 0 0 0.75rem 1.25rem;
}
/* text colours: >= 4.5:1 against white and the #f2f2f2 striped rows */
.note-text,
.pct {
  color: #495057;
}
.direction-enriched {
  color: #1e7e34;
  font-weight: bold;
}
.direction-depleted {
  color: #5a6268;
}
.direction-none {
  color: #495057;
}
.updated-note {
  background: #fff8e1;
  border-left: 4px solid #f0ad4e;
  padding: 0.5rem 0.75rem;
}
.help-box {
  background: #f8f9fa;
}
.help-box ul {
  padding-left: 1.25rem;
}
.container {
  max-width: 100%;
}
.form-bg {
  background: #f1efec;
}
fieldset {
  margin-bottom: 1rem;
}
.font-monospace,
.text-output,
.invalid-line {
  font-family: monospace;
}
.text-output {
  white-space: pre;
  overflow-x: auto;
  background: #f8f9fa;
  padding: 0.5rem;
}
.results-table th {
  background-color: #f8f9fa;
  white-space: nowrap;
}
.results-table td {
  vertical-align: top;
  white-space: nowrap;
}
.results-table td.seq-list {
  white-space: normal;
  min-width: 14rem;
}
.results-table tr.row-significant td:first-child {
  border-left: 3px solid #28a745;
}
.seq-list span {
  display: inline-block;
  white-space: nowrap;
}
</style>
